import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { electricProviderInfoService } from '../../../../services/electricProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('ElectricProviderInput');

const RATE_PLANS = [
  'Fixed Rate',
  'Variable Rate',
  'Time-of-Use',
  'Tiered Rate',
  'Net Metering',
  'Other',
];

const AUTOPAY_METHODS = [
  'Bank Account (ACH)',
  'Debit Card',
  'Credit Card',
  'PayPal',
  'Other',
];

export const ElectricProviderInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { electricProviderId?: string; isEdit?: boolean }>, string>>();
  const { electricProviderId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showRatePlanModal, setShowRatePlanModal] = useState(false);
  const [showAutopayMethodModal, setShowAutopayMethodModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedElectricProviderId, setSavedElectricProviderId] = useState<string | undefined>();
  
  const [formData, setFormData] = useState({
    companyName: '',
    accountNumber: '',
    serviceAddress: '',
    billingAddress: '',
    customerServicePhone: '',
    emergencyPhone: '',
    website: '',
    meterNumber: '',
    ratePlan: '',
    averageMonthlyCost: '',
    autopayEnabled: false,
    autopayMethod: '',
    contractEndDate: '',
    notes: '',
    username: '',
    password: '',
  });

  // Load existing electric provider data when editing
  useEffect(() => {
    if (isEdit && electricProviderId) {
      loadElectricProviderData();
    }
  }, [isEdit, electricProviderId]);

  const loadElectricProviderData = async () => {
    try {
      if (!electricProviderId) return;
      
      const data = await electricProviderInfoService.getElectricProviderInfoById(electricProviderId);
      if (data) {
        setFormData({
          companyName: data.companyName || '',
          accountNumber: '', // Will be loaded from vault
          serviceAddress: data.serviceAddress || '',
          billingAddress: data.billingAddress || '',
          customerServicePhone: data.customerServicePhone || '',
          emergencyPhone: data.emergencyPhone || '',
          website: data.website || '',
          meterNumber: data.meterNumber || '',
          ratePlan: data.ratePlan || '',
          averageMonthlyCost: data.averageMonthlyCost?.toString() || '',
          autopayEnabled: data.autopayEnabled || false,
          autopayMethod: data.autopayMethod || '',
          contractEndDate: data.contractEndDate || '',
          notes: data.notes || '',
          username: '',
          password: '',
        });

        // Load portal credentials
        try {
          const portalData = await vaultService.getPortalCredentials(electricProviderId, 'electric-provider');
          if (portalData) {
            setFormData(prev => ({
              ...prev,
              username: portalData.username || '',
              password: portalData.password || '',
              accountNumber: portalData.accountNumber || prev.accountNumber,
            }));
            setHasOnlinePortal(true);
          }
        } catch (vaultError) {
          logger.debug('Could not load portal credentials:', vaultError);
        }
      }
    } catch (error) {
      logger.error('Error loading electric provider data:', error);
      Alert.alert('Error', 'Failed to load electric provider information');
    }
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return cleaned;
    }
  };

  // Helper: format currency as $x.xx while typing
  const formatCurrency = (text: string) => {
    const cleaned = text.replace(/[^0-9.]/g, '');
    return cleaned ? `$${cleaned}` : '';
  };

  // Helper: format date as mm/dd/yyyy
  const formatDate = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length >= 8) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    } else if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    } else {
      return cleaned;
    }
  };

  const handlePhoneChange = (field: string, text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.accountNumber) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      
      // Basic validation
      if (!formData.companyName) {
        Alert.alert('Validation Error', 'Company name is required');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        companyName: formData.companyName,
        serviceAddress: formData.serviceAddress || undefined,
        billingAddress: formData.billingAddress || undefined,
        customerServicePhone: formData.customerServicePhone.replace(/\D/g, '') || undefined,
        emergencyPhone: formData.emergencyPhone.replace(/\D/g, '') || undefined,
        website: formData.website || undefined,
        meterNumber: formData.meterNumber || undefined,
        ratePlan: formData.ratePlan || undefined,
        averageMonthlyCost: formData.averageMonthlyCost ? parseFloat(formData.averageMonthlyCost.replace(/[^0-9.]/g, '')) : undefined,
        autopayEnabled: formData.autopayEnabled,
        autopayMethod: formData.autopayMethod || undefined,
        contractEndDate: formData.contractEndDate || undefined,
        notes: formData.notes || undefined,
      };

      logger.debug('[Electric Provider Debug] Payload being sent:', payload);
      logger.debug('[Electric Provider Debug] Account number value:', formData.accountNumber);

      // Create or update electric provider record
      let saved;
      let savedId;

      if (isEdit && electricProviderId) {
        saved = await electricProviderInfoService.updateElectricProviderInfo(electricProviderId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = saved?.data || saved;
        savedId = updatedData.id || electricProviderId;
      } else {
        saved = await electricProviderInfoService.createElectricProviderInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }
      
      // Store the saved ID for the Done button
      setSavedElectricProviderId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password || formData.accountNumber)) {
        try {
          logger.debug('[Portal Debug] Storing portal credentials in vault for electric provider ID:', savedId);
          await vaultService.storePortalCredentials(savedId, 'electric-provider', {
            username: formData.username,
            password: formData.password,
            accountNumber: formData.accountNumber,
          });
          logger.debug('[Portal Debug] Successfully stored portal credentials in vault');
        } catch (e) {
          logger.error('[Portal Debug] Vault write failed for electric provider portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Electric provider information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('ElectricProviderLockUnlock', { electricProviderId: electricProviderId })
          }
        ]);
      } else {
        // Clear the form and reset states for Add Another
        setFormData({
          companyName: '',
          accountNumber: '',
          serviceAddress: '',
          billingAddress: '',
          customerServicePhone: '',
          emergencyPhone: '',
          website: '',
          meterNumber: '',
          ratePlan: '',
          averageMonthlyCost: '',
          autopayEnabled: false,
          autopayMethod: '',
          contractEndDate: '',
          notes: '',
          username: '',
          password: '',
        });
        setHasOnlinePortal(false);
        
        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
      }
    } catch (error: any) {
      logger.error('Error saving electric provider info:', error);
      Alert.alert('Error', 'Failed to save electric provider information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      companyName: '',
      accountNumber: '',
      serviceAddress: '',
      billingAddress: '',
      customerServicePhone: '',
      emergencyPhone: '',
      website: '',
      meterNumber: '',
      ratePlan: '',
      averageMonthlyCost: '',
      autopayEnabled: false,
      autopayMethod: '',
      contractEndDate: '',
      notes: '',
      username: '',
      password: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    (navigation as any).navigate('ElectricProviderLockUnlock', { electricProviderId: savedElectricProviderId });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEdit ? 'Edit Electric Provider Information' : 'Electric Provider Information'}</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.companyName}
              onChangeText={(text) => setFormData({ ...formData, companyName: text })}
              placeholder="Enter company name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account Number 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.accountNumber}
                onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                placeholder="Enter Account Number"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showAccountNumber}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowAccountNumber(!showAccountNumber)}
              >
                <Image
                  source={showAccountNumber ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Address</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.serviceAddress}
              onChangeText={(text) => setFormData({ ...formData, serviceAddress: text })}
              placeholder="Address where electric service is provided"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Billing Address</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.billingAddress}
              onChangeText={(text) => setFormData({ ...formData, billingAddress: text })}
              placeholder="Address where bills are sent (if different from service address)"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Customer Service Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.customerServicePhone}
              onChangeText={(text) => handlePhoneChange('customerServicePhone', text)}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.emergencyPhone}
              onChangeText={(text) => handlePhoneChange('emergencyPhone', text)}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.input}
              value={formData.website}
              onChangeText={(text) => setFormData({ ...formData, website: text.toLowerCase() })}
              placeholder="Enter Website URL"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your electric provider have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Meter Number</Text>
            <TextInput
              style={styles.input}
              value={formData.meterNumber}
              onChangeText={(text) => setFormData({ ...formData, meterNumber: text })}
              placeholder="Enter meter number"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Rate Plan</Text>
            <TouchableOpacity 
              style={styles.dropdownButton}
              onPress={() => setShowRatePlanModal(true)}
            >
              <Text style={[styles.dropdownText, { color: formData.ratePlan ? '#1C2C4F' : '#A9A9A9' }]}>
                {formData.ratePlan || 'Select rate plan'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Average Monthly Cost</Text>
            <TextInput
              style={styles.input}
              value={formData.averageMonthlyCost}
              onChangeText={(text) => setFormData({ ...formData, averageMonthlyCost: formatCurrency(text) })}
              placeholder="$0.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Autopay Enabled</Text>
              <TouchableOpacity
                style={styles.toggleButton}
                onPress={() => setFormData({ ...formData, autopayEnabled: !formData.autopayEnabled })}
              >
                <View style={[styles.toggleCircle, formData.autopayEnabled && styles.toggleCircleActive]}>
                  <View style={[styles.toggleDot, formData.autopayEnabled && styles.toggleDotActive]} />
                </View>
              </TouchableOpacity>
            </View>
          </View>

          {formData.autopayEnabled && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Autopay Method</Text>
              <TouchableOpacity 
                style={styles.dropdownButton}
                onPress={() => setShowAutopayMethodModal(true)}
              >
                <Text style={[styles.dropdownText, { color: formData.autopayMethod ? '#1C2C4F' : '#A9A9A9' }]}>
                  {formData.autopayMethod || 'Select autopay method'}
                </Text>
                <Image
                  source={require('../../../../assets/icons/down.png')}
                  style={styles.dropdownIcon}
                />
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contract End Date</Text>
            <TextInput
              style={styles.input}
              value={formData.contractEndDate}
              onChangeText={(text) => setFormData({ ...formData, contractEndDate: formatDate(text) })}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>


          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Enter any additional notes"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          {!showAddAnotherDone ? (
            <View style={styles.buttonContainer}>
              {submitting ? (
                <ActivityIndicator size="large" color="#29BFC0" />
              ) : (
                <TouchableOpacity 
                  style={styles.submitButton} 
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <View style={styles.addAnotherContainer}>
              <TouchableOpacity 
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.addAnotherButtonText}>Add Another Electric Provider</Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.doneButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      {/* Rate Plan Modal */}
      <Modal
        visible={showRatePlanModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Rate Plan</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowRatePlanModal(false)}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.optionsList}>
              {RATE_PLANS.map((plan, index) => (
                <TouchableOpacity
                  key={index}
                  style={styles.optionItem}
                  onPress={() => {
                    setFormData({ ...formData, ratePlan: plan });
                    setShowRatePlanModal(false);
                  }}
                >
                  <Text style={styles.optionItemText}>{plan}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Autopay Method Modal */}
      <Modal
        visible={showAutopayMethodModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Autopay Method</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowAutopayMethodModal(false)}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.optionsList}>
              {AUTOPAY_METHODS.map((method, index) => (
                <TouchableOpacity
                  key={index}
                  style={styles.optionItem}
                  onPress={() => {
                    setFormData({ ...formData, autopayMethod: method });
                    setShowAutopayMethodModal(false);
                  }}
                >
                  <Text style={styles.optionItemText}>{method}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(10),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(10),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: ms(10),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    padding: ms(20),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  vaultText: {
    fontSize: ms(12, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontStyle: 'italic',
    marginLeft: ms(2),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#A9A9A9',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggleButton: {
    marginLeft: 'auto',
  },
  toggle: {
    width: s(50),
    height: s(24),
    borderRadius: ms(12),
    backgroundColor: '#d8dbdd',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingHorizontal: ms(2),
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
    alignItems: 'flex-end',
  },
  toggleThumb: {
    width: s(20),
    height: s(20),
    borderRadius: ms(10),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toggleThumbActive: {},
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  toggleCircle: {
    width: s(50),
    height: s(24),
    borderRadius: ms(12),
    backgroundColor: '#d8dbdd',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingHorizontal: ms(2),
  },
  toggleCircleActive: {
    backgroundColor: '#29BFC0',
    alignItems: 'flex-end',
  },
  toggleDot: {
    width: s(20),
    height: s(20),
    borderRadius: ms(10),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toggleDotActive: {},
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -8 }],
    padding: ms(5),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  dropdownButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownText: {
    fontSize: ms(16, 0.3),
    flex: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: ms(20),
    paddingHorizontal: ms(20),
    marginBottom: ms(100),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(30),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    width: '100%',
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherContainer: {
    flexDirection: 'column',
    paddingVertical: ms(20),
    paddingHorizontal: ms(20),
    marginBottom: ms(100),
    gap: ms(15),
  },
  addAnotherButton: {
    backgroundColor: '#F2994A',
    borderRadius: ms(30),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    width: '100%',
    alignItems: 'center',
  },
  addAnotherButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(30),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    width: '100%',
    alignItems: 'center',
  },
  doneButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  optionsList: {
    padding: ms(16),
  },
  optionItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  optionItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});